import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Avatar,
  Button,
  Chip,
  Stack,
  Divider,
  CircularProgress,
  Paper,
} from '@mui/material';
import StoreIcon from '@mui/icons-material/Store';
import LocationIcon from '@mui/icons-material/LocationOn';
import StarIcon from '@mui/icons-material/Star';
import VerifiedIcon from '@mui/icons-material/Verified';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DealIcon from '@mui/icons-material/Handshake';
import LockIcon from '@mui/icons-material/Lock';
import {
  Tooltip,
} from '@mui/material';
import { supabase } from '../services/supabaseClient';
import { DataService } from '../services/dataService';
import ProductCardEnhanced from '../components/market/ProductCardEnhanced';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';

const ShopProfile: React.FC = () => {
  const { shopSlug } = useParams<{ shopSlug: string }>();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { toggleWishlist, isInWishlist } = useWishlist();
  const [shop, setShop] = useState<any>(null);
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchShopData = async () => {
      setLoading(true);
      try {
        // 1. Fetch Shop Details
        const { data: shopData, error: shopError } = await supabase
          .from('shops')
          .select('*')
          .eq('slug', shopSlug)
          .single();

        if (shopError) throw shopError;
        setShop(shopData);

        // 2. Fetch Shop Products
        const shopProducts = await DataService.getProducts({ shopId: shopData.id });
        setProducts(shopProducts);
      } catch (error) {
        console.error('Error fetching shop data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (shopSlug) fetchShopData();
  }, [shopSlug]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!shop) {
    return (
      <Container sx={{ py: 10, textAlign: 'center' }}>
        <Typography variant="h4">Shop not found</Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/marketplace')} sx={{ mt: 2 }}>
          Back to Marketplace
        </Button>
      </Container>
    );
  }

  return (
    <Box sx={{ pb: 8 }}>
      {/* Banner Section */}
      <Box sx={{ position: 'relative', height: { xs: 200, md: 350 }, bgcolor: 'grey.200' }}>
        {shop.banner_url ? (
          <Box
            component="img"
            src={shop.banner_url}
            alt={shop.name}
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <Box sx={{ width: '100%', height: '100%', bgcolor: 'primary.dark', opacity: 0.8 }} />
        )}
        
        <Container sx={{ position: 'relative', height: '100%' }}>
          <Box
            sx={{
              position: 'absolute',
              bottom: -60,
              left: { xs: '50%', md: 24 },
              transform: { xs: 'translateX(-50%)', md: 'none' },
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'center', md: 'flex-end' },
              gap: 3,
              width: '100%'
            }}
          >
            <Avatar
              src={shop.logo_url}
              sx={{
                width: 120,
                height: 120,
                border: '4px solid white',
                boxShadow: 3,
                bgcolor: 'white'
              }}
            >
              <StoreIcon sx={{ fontSize: 60 }} />
            </Avatar>
            <Box sx={{ mb: { xs: 0, md: 1 }, textAlign: { xs: 'center', md: 'left' }, flexGrow: 1 }}>
              <Typography variant="h3" fontWeight="bold" sx={{ color: { xs: 'text.primary', md: 'white' }, textShadow: { md: '0px 2px 4px rgba(0,0,0,0.5)' } }}>
                {shop.name}
                {shop.status === 'active' && <VerifiedIcon sx={{ ml: 1, color: 'info.main', verticalAlign: 'middle' }} />}
              </Typography>
              <Stack direction="row" spacing={2} sx={{ color: { xs: 'text.secondary', md: 'rgba(255,255,255,0.9)' }, justifyContent: { xs: 'center', md: 'flex-start' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <StarIcon fontSize="small" sx={{ color: 'warning.main' }} />
                  <Typography variant="body2">{shop.rating || 0} ({shop.review_count || 0} reviews)</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LocationIcon fontSize="small" />
                  <Typography variant="body2">Kenya</Typography>
                </Box>
              </Stack>
            </Box>
            <Box sx={{ mb: 1, pr: { md: 4 }, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button variant="contained" startIcon={<ShoppingBagIcon />} sx={{ borderRadius: '20px', px: 4 }}>
                Follow Shop
              </Button>
              <Button 
                variant="outlined" 
                startIcon={<DealIcon />} 
                sx={{ 
                  borderRadius: '20px', 
                  px: 4, 
                  bgcolor: 'rgba(255,255,255,0.9)',
                  '&:hover': { bgcolor: 'white' }
                }}
                onClick={() => navigate('/dashboard/deals')}
              >
                Negotiate Deal
              </Button>
              <Tooltip title="All transactions are secured by Farmers HUB Escrow.">
                <Chip 
                  icon={<LockIcon sx={{ '&&': { fontSize: 14, color: 'white' } }} />} 
                  label="Secure Trading" 
                  size="small" 
                  sx={{ bgcolor: 'rgba(0,0,0,0.5)', color: 'white', border: 'none' }} 
                />
              </Tooltip>
            </Box>
          </Box>
        </Container>
      </Box>

      <Container sx={{ mt: 10 }}>
        <Grid container spacing={4}>
          {/* Shop Sidebar */}
          <Grid item xs={12} md={3}>
            <Stack spacing={3}>
              <Paper sx={{ p: 3, borderRadius: '16px' }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>About Shop</Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  {shop.description || 'This shop has not provided a description yet.'}
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>Response Rate</Typography>
                <Typography variant="body2" color="success.main" fontWeight="bold">98% (Very Fast)</Typography>
              </Paper>
              
              <Paper sx={{ p: 3, borderRadius: '16px', bgcolor: 'primary.main', color: 'white' }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>Seasonal Offer</Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }} paragraph>
                  Save up to 20% on all seeds during the harvest season!
                </Typography>
                <Button variant="contained" sx={{ bgcolor: 'white', color: 'primary.main', '&:hover': { bgcolor: 'grey.100' } }}>
                  View Sale
                </Button>
              </Paper>
            </Stack>
          </Grid>

          {/* Product Grid */}
          <Grid item xs={12} md={9}>
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h5" fontWeight="bold">Shop Products ({products.length})</Typography>
              <Stack direction="row" spacing={1}>
                <Chip label="All" clickable color="primary" />
                <Chip label="Seeds" clickable variant="outlined" />
                <Chip label="Fertilizer" clickable variant="outlined" />
              </Stack>
            </Box>

            {products.length === 0 ? (
              <Paper sx={{ py: 10, textAlign: 'center', borderRadius: '16px', bgcolor: 'background.paper', border: '1px dashed grey' }}>
                <ShoppingBagIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">No products found</Typography>
                <Typography variant="body2" color="text.secondary">This shop hasn't listed any products yet.</Typography>
              </Paper>
            ) : (
              <Grid container spacing={3}>
                {products.map((product) => (
                  <Grid key={product.id} item xs={12} sm={6} lg={4}>
                    <ProductCardEnhanced 
                      product={product} 
                      onAddToCart={addToCart}
                      onToggleFavorite={() => toggleWishlist(product)}
                      isFavorite={isInWishlist(product.id)}
                      onQuickView={() => navigate(`/marketplace/product/${product.id}`)}
                    />
                  </Grid>
                ))}
              </Grid>
            )}
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default ShopProfile;
